import React from 'react';
import PropTypes from 'prop-types';

import { Button, Field } from '@ontruck/orc';

import FieldError from 'components/form/FieldError';
import Modal from 'components/layout/Modal';
import Select from 'components/CustomSelect';

import { AddressModalHeaderStyled } from 'components/AddressModal/styled';
import AddressCompanyNameField from 'components/AddressModal/components/AddressCompanyNameField';
import AddressLineField from 'components/AddressModal/components/AddressLineField';
import { constants as addressesConstants } from 'utils/addresses';
import cx from 'classnames';

const AddressModal = ({
  address_line,
  city,
  country,
  shipperCountry,
  className,
  company_name,
  contact_name,
  contact_phone,
  errors,
  handleClose,
  handleFieldChange,
  handleCountryChange,
  countryOptions,
  handleSubmit,
  handleZipcodeChange,
  isNewAddress,
  names,
  t,
  warnings,
  zipcode_city,
  zipcode,
  canDelete,
  handleSubmitDelete,
  handleCompanyNameChange,
  handleAddressLineChange,
  handleSuggestionSelected,
}) => {
  const isFrenchAddress = country === 'FR';
  const isSpanishShipper = shipperCountry === 'ES';
  const showCityNameNextToZipcode =
    !!zipcode_city && !isFrenchAddress && !isSpanishShipper;
  const showCityNameUnderZipcode =
    !!zipcode_city && !isFrenchAddress && isSpanishShipper;
  const showCityNamePlaceholder =
    !zipcode_city && !isFrenchAddress && isSpanishShipper;
  const options = countryOptions.map((opt) => ({
    value: opt.value,
    label: t(`countries.${opt.value}`),
  }));

  return (
    <Modal
      show
      size="S"
      title={<AddressModalHeaderStyled isNewAddress={isNewAddress} t={t} />}
      onClose={handleClose}
    >
      <form className={className} autoComplete="off">
        <div className="field-container zipcode-container">
          <div className="cell">
            <Field
              type="text"
              id={names.zipcode}
              name={names.zipcode}
              label={t('label_zipcode')}
              onChange={handleZipcodeChange}
              size="L"
              fullWidth
              invalid={!!warnings.zipcode}
              error={!!errors.zipcode}
              value={zipcode}
            />
            {isSpanishShipper && (
              <Select
                value={options.find((option) => option.value === country)}
                label={t('country')}
                placeholder={t('country')}
                options={options}
                isSearchable={false}
                onChange={handleCountryChange}
                t={t}
              />
            )}
            {showCityNameNextToZipcode && (
              <div className="cell city-name">{zipcode_city}</div>
            )}
            {!!errors.zipcode && (
              <FieldError className={cx('error')}>
                {t(errors.zipcode)}
              </FieldError>
            )}
          </div>
        </div>
        {showCityNamePlaceholder && (
          <div className="cell city-name">{t('postal_code_placeholder')}</div>
        )}
        {showCityNameUnderZipcode && (
          <div className="cell city-name">{zipcode_city}</div>
        )}
        {isFrenchAddress && (
          <div className="field-container">
            <Field
              type="text"
              id={names.city}
              name={names.city}
              label={t('label_city')}
              onChange={handleFieldChange}
              size="L"
              fullWidth
              invalid={!!warnings.city}
              error={!!errors.city}
              value={city}
            />
          </div>
        )}

        <div className="field-container">
          <AddressCompanyNameField
            zipcode={zipcode}
            country={country}
            city={zipcode_city}
            error={!!errors.company_name}
            invalid={!!warnings.company_name}
            companyName={company_name}
            onChange={handleCompanyNameChange}
            onSuggestionClick={handleSuggestionSelected}
          />
        </div>

        <div className="field-container">
          <AddressLineField
            zipcode={zipcode}
            country={country}
            city={zipcode_city}
            error={!!errors.address_line}
            invalid={!!warnings.address_line}
            addressLine={address_line}
            onChange={handleAddressLineChange}
            onSuggestionClick={handleSuggestionSelected}
          />
        </div>
        <div className="field-container">
          <Field
            type="text"
            id={names.contact_name}
            name={names.contact_name}
            label={t('label_contact_name')}
            onChange={handleFieldChange}
            size="L"
            fullWidth
            invalid={!!warnings.contact_name}
            error={!!errors.contact_name}
            value={contact_name}
          />
        </div>
        <div className="field-container">
          <Field
            type="tel"
            id={names.contact_phone}
            name={names.contact_phone}
            label={t('label_contact_phone')}
            onChange={handleFieldChange}
            maxLength={15}
            size="L"
            fullWidth
            invalid={!!warnings.contact_phone}
            error={!!errors.contact_phone}
            value={contact_phone}
          />
          {!!errors.contact_phone && (
            <FieldError className="error">{t(errors.contact_phone)}</FieldError>
          )}
        </div>
        <Button
          onClick={handleSubmit}
          text={t(`${isNewAddress ? 'apply' : 'modify'}`)}
          fullWidth
        />
        {isNewAddress && (
          <Button
            onClick={handleClose}
            text={t('cancel')}
            fullWidth
            nude
            skin="secondary"
          />
        )}

        {canDelete && !isNewAddress && (
          <div className="cancel-btn">
            <Button
              onClick={handleSubmitDelete}
              text={t('delete')}
              fullWidth
              skin="secondary"
            />
          </div>
        )}
      </form>
    </Modal>
  );
};

AddressModal.propTypes = {
  t: PropTypes.func.isRequired,
  handleZipcodeChange: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  handleCompanyNameChange: PropTypes.func.isRequired,
  handleAddressLineChange: PropTypes.func.isRequired,
  names: addressesConstants.PROP_TYPES.address,
  warnings: addressesConstants.PROP_TYPES.address,
  errors: addressesConstants.PROP_TYPES.address,
  className: PropTypes.string.isRequired,
  isNewAddress: PropTypes.bool,
  zipcode_city: PropTypes.string,
  showZipCodeCity: PropTypes.bool,
  city: PropTypes.string,
  showCityField: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  zipcode: PropTypes.string,
  company_name: PropTypes.string,
  address_line: PropTypes.string,
  contact_name: PropTypes.string,
  contact_phone: PropTypes.string,
  canDelete: PropTypes.bool,
  handleSubmitDelete: PropTypes.bool,
  handleSuggestionSelected: PropTypes.func,
};

AddressModal.defaultProps = {
  names: {},
  warnings: {},
  errors: {},
  isNewAddress: true,
  zipcode_city: '',
  showZipCodeCity: true,
  city: '',
  showCityField: false,
  zipcode: '',
  company_name: '',
  address_line: '',
  contact_name: '',
  contact_phone: '',
  country: '',
  handleClose: () => {},
  handleSubmit: () => {},
  canDelete: false,
  handleSubmitDelete: () => {},
  handleSuggestionSelected: () => {},
};

export default AddressModal;
